/**
 * The New Connection dialog's navigation (Option C, C1 + C3): the view (start ↔ form), the start filter, focus after each
 * navigation (`dialogFocus.ts`), the polite announcements and Escape (plan §3.6).
 *
 * - `pick(engine, source)` — a card, the filter's Enter, a rail item or the compact select: the draft contract selects
 *   the engine (restore or seed); re-picking the current engine in the form is a no-op.
 * - `continuePaste(result)` — C3: Continue on the start view's detection card (or Enter in the filter). The string is
 *   applied through the draft contract, the filter clears and the engine's form opens; focus goes to the first
 *   required empty field. `applyPanePaste(result)` is the form view's Apply: the same, from the paste field.
 * - `pasteFromClipboard()` — the start view's Paste connection string: an explicit click reads the clipboard into the
 *   filter when it holds a connection string; otherwise the filter gets focus with a paste hint (the clipboard text is
 *   never put anywhere).
 * - `applyAzurePick(resource)` — C3.1: a Browse Azure pick, from the top-level entry (start view, rail, Add from…) or the
 *   form's own button. The authoritative patch goes through the draft contract (`applyPatch(engine, patch, 'azure_pick')`:
 *   the resource's engine is selected first, another engine's draft restored or seeded, the one on screen kept), the
 *   start view leaves for the form through `showView` (the C1b transition plays), an engine change in the form is
 *   announced and copied first, and focus goes to the first required empty field.
 * - `back(via)` — All engines, or Escape in a create-mode form (D11): the draft is kept, pending Tests / Loads / Saves
 *   are voided (`leaveToStart`), and the filter gets focus with the engine the user left as the active option.
 * C1b: every view change goes through `showView`, which captures the view being left before the state update
 * (`motion.capture`), so the transition (`transition/useSharedLayoutTransition.ts`) plays after the commit — a new way
 * into or out of the form (C3's Continue from a pasted string) animates without doing anything. Only a change the user
 * did not navigate opts out (`animate: false`: a save that keeps the dialog open). An engine switch in the form copies
 * the pane first (`motion.captureSwap`) — so does a pane Apply that switches the engine (C3); a filter change (typed,
 * pasted, read from the clipboard or handed over by the palette) and Escape settle a running flight (`motion.settle`).
 */
import { useCallback, useLayoutEffect, useState } from 'react';
import type { UserFacingEngineId } from '@/core/engines';
import { parseConnectionString, type ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import { clipboardConnectionText, readClipboardText } from '../../services/connection-string/clipboardPreview';
import type { AzureDiscoveredResource } from '../../../../types/azureDiscovery';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { azureResourceToPatch } from '../state/azureResourceToPatch';
import type { ConnectionForm } from '../state/connectionForm';
import { findEngineCatalogEntry } from '../state/engineCatalog';
import { escapeStep, formView, isManualPick, START_VIEW, type DialogView, type EngineSource } from '../state/dialogView';
import type { useEngineDrafts } from '../state/useEngineDrafts';
import { engineOptionId, firstEngineOption, filterEngines, startOptionOrder, type StartOptionId } from '../start/engineFilter';
import { appliedAzurePick, looksLikeConnectionString } from '../paste/pastePreview';
import { focusDialogTarget, type FocusTarget } from './dialogFocus';
import type { useConnectionDialogFunnel } from './useConnectionDialogFunnel';
import type { PasteEntry } from './usePasteEntry';
import { useSharedLayoutTransition } from '../transition/useSharedLayoutTransition';

type Options = {
  isEditMode: boolean;
  initialView: DialogView;
  form: ConnectionForm;
  drafts: Pick<ReturnType<typeof useEngineDrafts>, 'selectEngine' | 'leaveToStart' | 'applyPatch' | 'baseValuesFor'>;
  paste: PasteEntry;
  funnel: ReturnType<typeof useConnectionDialogFunnel>;
  /** The dialog content element (the transition measures inside it) and the form's engine. */
  root: HTMLElement | null;
  engine: DatabaseEngine;
};

/**
 * How the filter text got there: typed, pasted (a paste event, or the Paste entry's clipboard read), or handed over by
 * the command palette's New Connection from Clipboard (which already reported its parse).
 */
export type FilterVia = 'type' | 'paste' | 'prefill';

export function useDialogNavigation({ isEditMode, initialView, form, drafts, paste, funnel, root, engine: formEngine }: Options) {
  const [view, setViewState] = useState<DialogView>(initialView);
  const [filter, setFilterState] = useState('');
  const [filterVia, setFilterVia] = useState<FilterVia>('type');
  const [pasteHint, setPasteHint] = useState(false);
  const [returnActive, setReturnActive] = useState<StartOptionId | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; seq: number } | null>(null);

  // A repeated message is made distinct (a zero-width space), so a live region announces it again.
  const announce = useCallback((text: string) => setAnnouncement((previous) => (previous === text ? `${text}\u200B` : text)), []);
  const requestFocus = (target: FocusTarget) => setFocusRequest((previous) => ({ target, seq: (previous?.seq ?? 0) + 1 }));

  // After the navigation committed, so the target view's controls exist.
  useLayoutEffect(() => {
    if (focusRequest) focusDialogTarget(focusRequest.target, form.state.values);
    // Only a new request moves focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);
  // Declared after the focus effect: focus moves before the flight measures and starts (plan §3.2).
  const motion = useSharedLayoutTransition({ root, view: view.kind, engine: formEngine });

  /**
   * The ONLY way the view changes. A change of view kind captures the view being left first, so it animates; the
   * capture reads the DOM as it still is — nothing commits until this event's handlers return.
   */
  const showView = (next: DialogView, { animate = true }: { animate?: boolean } = {}) => {
    if (animate && next.kind !== view.kind) motion.capture(next.kind);
    setViewState(next);
  };

  const announceForm = (engine: DatabaseEngine) => announce(`${findEngineCatalogEntry(engine)?.label ?? engine} connection form`);

  const enterForm = (engine: DatabaseEngine, source: EngineSource) => {
    const from = view.kind;
    showView(formView(source));
    funnel.engineSelected(engine, source, from);
    announceForm(engine);
    requestFocus(isManualPick(source) ? 'name' : 'prefill');
  };

  const pick = (engine: DatabaseEngine, source: EngineSource) => {
    if (view.kind === 'form' && engine !== form.state.values.engine) motion.captureSwap(engine);
    // A card picked under a refused paste (UX-02): the string was a paste, not a filter — it does not wait in the filter.
    if (view.kind === 'start' && looksLikeConnectionString(filter)) setFilterState('');
    const changed = drafts.selectEngine(engine, source);
    if (!changed && view.kind === 'form') return;
    enterForm(engine, source);
  };

  const setFilter = (text: string, via: FilterVia = 'type') => {
    // A keystroke, paste or IME commit during a flight: the cards take the filtered layout with no stale transform (C1b).
    motion.settle();
    if (text.trim() && via !== 'prefill') funnel.filtered(filterEngines(text).any);
    setFilterState(text);
    setFilterVia(via);
    setPasteHint(false);
  };

  const continuePaste = (result: ConnectionStringParseResult) => {
    const engine = paste.apply(result, 'start_filter', funnel.lastEngine());
    if (!engine) return;
    setFilterState('');
    enterForm(engine, 'paste');
  };

  const applyPanePaste = (result: ConnectionStringParseResult) => {
    const before = form.state.values.engine;
    // An Apply that switches the engine is an engine switch in the form: the pane is copied first, as for a pick (C1b).
    if (view.kind === 'form' && result.engine && result.engine !== before) motion.captureSwap(result.engine);
    const engine = paste.apply(result, 'pane_field', before);
    if (!engine) return;
    paste.closeField();
    if (engine !== before) {
      funnel.engineSelected(engine, 'paste', 'form');
      announceForm(engine);
    }
    requestFocus('prefill');
  };

  const applyAzurePick = (resource: AzureDiscoveredResource) => {
    const engine: DatabaseEngine = resource.engine;
    const before = view.kind === 'form' ? form.state.values.engine : null;
    // Another engine picked in the form: the pane is copied first, as for a rail pick or a pane Apply (C1b).
    if (before && engine !== before) motion.captureSwap(engine);
    const patch = azureResourceToPatch(resource, form.state.values.alias);
    // The pane says what the pick filled (UX-01): measured against the draft it lands on, before the patch.
    const summary = appliedAzurePick(resource.name, engine, patch, drafts.baseValuesFor(engine));
    drafts.applyPatch(engine, patch, 'azure_pick');
    paste.noteApplied(summary);
    if (view.kind === 'start') {
      setFilterState('');
      enterForm(engine, 'azure_pick');
      return;
    }
    if (engine !== before) {
      funnel.engineSelected(engine, 'azure_pick', 'form');
      announceForm(engine);
    }
    requestFocus('prefill');
  };

  // The palette's clipboard handoff without an engine (ruling after C3 review r6): never an empty dialog — its text goes
  // into the filter (the card asks which engine, or says why it was refused), or the paste hint shows.
  useLayoutEffect(() => {
    const prefill = paste.startPrefill;
    if (!prefill) return;
    if ('text' in prefill) setFilter(prefill.text, 'prefill');
    else setPasteHint(true);
    requestFocus('filter');
    // Once, for the handoff this dialog was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paste.startPrefill]);

  const pasteFromClipboard = async () => {
    // Line breaks are dropped the way a text input drops them from a paste.
    const text = clipboardConnectionText(await readClipboardText());
    const parsed = text ? parseConnectionString(text) : undefined;
    // A string the rules refused, or one several engines read alike, is a connection string too: the filter shows why, or
    // asks which engine (ruling after C3 review r5).
    if (parsed && (parsed.engine || parsed.refusal || parsed.choices)) setFilter(text, 'paste');
    else setPasteHint(true);
    requestFocus('filter');
  };

  const back = (via: 'button' | 'escape') => {
    if (isEditMode || view.kind !== 'form') return;
    const engine = form.state.values.engine;
    drafts.leaveToStart();
    paste.closeField();
    const left = engineOptionId(engine as UserFacingEngineId);
    setReturnActive(startOptionOrder(filter).includes(left) ? left : firstEngineOption(filter));
    showView(START_VIEW);
    funnel.engineBack(engine, via);
    announce('Back to all engines');
    requestFocus('filter');
  };

  /** Radix's Escape, once no nested layer took it: preventDefault keeps the dialog open for every step but "close". */
  const onEscape = (event: { preventDefault: () => void }) => {
    const step = escapeStep({ isEditMode, view: view.kind, filter, pasteOpen: paste.open });
    if (step === 'close') return;
    event.preventDefault();
    if (step === 'back-to-start') return back('escape');
    // A running flight settles first (C1b), then C3's step.
    motion.settle();
    if (step === 'close-paste') {
      paste.closeField('escape');
      return requestFocus('paste-entry');
    }
    setFilterState('');
    setPasteHint(false);
    requestFocus('filter');
  };

  /** A save that keeps the dialog open (no connections yet): a new draft starts on the start view. */
  const restartAfterSave = () => {
    if (isEditMode) return;
    showView(START_VIEW, { animate: false });
    setFilterState('');
    setReturnActive(null);
    paste.reset();
    requestFocus('filter');
  };

  return {
    view,
    filter,
    filterVia,
    setFilter,
    pasteHint,
    returnActive,
    announcement,
    announce,
    pick,
    continuePaste,
    applyPanePaste,
    applyAzurePick,
    pasteFromClipboard,
    closePasteField: () => {
      paste.closeField('button');
      requestFocus('paste-entry');
    },
    back,
    onEscape,
    restartAfterSave,
  };
}

export type DialogNavigation = ReturnType<typeof useDialogNavigation>;
